import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../components/ui";
import {
  MAX_AI_POST_CHARS,
  aiContentApproveAll,
  aiContentApprovePost,
  aiContentGeneratePlan,
  aiContentGetCurrentPlan,
  aiContentPausePlan,
  aiContentRegeneratePost,
  aiContentSkipPost,
  aiContentUnskipPost,
  aiContentUpdatePost,
  aiContentPublishNow,
  getStoredAiContentPlanView,
  setStoredAiContentPlanView,
  type AiContentPlan,
  type AiContentPost,
  type GenerationsInfo,
} from "../../utils/aiContent";
import type { AiContentOutletContext } from "./AiContentLayout";
import { aiOutlineBtn, aiPrimaryBtn, aiActionOutlineBtn, aiActionPrimaryBtn, aiActionGhostBtn } from "./aiContentUi";

const statusLabel = (status: string) =>
  String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const statusClass = (status: string) => {
  switch (status) {
    case "published":
      return "bg-success-bg text-success";
    case "scheduled":
    case "approved":
      return "bg-bg-light-blue text-brand-blue";
    case "failed":
      return "bg-danger/10 text-danger";
    case "skipped":
      return "bg-light-grey text-muted";
    default:
      return "bg-mini-avatar text-mini-avatar-fg";
  }
};

/** datetime-local needs local wall-clock, not UTC from toISOString(). */
const toDatetimeLocalValue = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const PlanPage = () => {
  const navigate = useNavigate();
  const { ownerId, userId, workspaceId } =
    useOutletContext<AiContentOutletContext>();
  const scope = { ownerId, userId, workspaceId };

  const [plan, setPlan] = useState<AiContentPlan | null>(null);
  const [posts, setPosts] = useState<AiContentPost[]>([]);
  const [generations, setGenerations] = useState<GenerationsInfo | null>(null);
  const [view, setView] = useState<"list" | "calendar">(() =>
    getStoredAiContentPlanView()
  );
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [editPost, setEditPost] = useState<AiContentPost | null>(null);
  const [editText, setEditText] = useState("");
  const [editScheduledAt, setEditScheduledAt] = useState("");
  const [confirm, setConfirm] = useState<null | {
    kind:
      | "approve"
      | "skip"
      | "approve_all"
      | "generate"
      | "regenerate"
      | "pause"
      | "resume"
      | "publish_now";
    post?: AiContentPost;
    title: string;
    message: string;
    confirmLabel: string;
    danger?: boolean;
  }>(null);

  const closeConfirm = () => setConfirm(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await aiContentGetCurrentPlan(scope);
      setPlan(data?.plan || null);
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
      if (data?.generations) setGenerations(data.generations);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to load plan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ownerId) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const attentionCount = useMemo(
    () => posts.filter((p) => p.status === "failed").length,
    [posts]
  );

  const openEdit = (post: AiContentPost) => {
    setEditPost(post);
    setEditText(post.postText || "");
    setEditScheduledAt(toDatetimeLocalValue(post.scheduledAt));
  };

  const regenerate = async (post: AiContentPost) => {
    setBusyId(`regen-${post._id}`);
    try {
      await aiContentRegeneratePost(scope, post._id);
      toast.success(`Day ${post.dayIndex} regenerated`);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Regenerate failed");
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (post: AiContentPost) => {
    setBusyId(`approve-${post._id}`);
    try {
      await aiContentApprovePost(scope, post._id);
      toast.success(`Day ${post.dayIndex} approved and scheduled`);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const skip = async (post: AiContentPost) => {
    setBusyId(`skip-${post._id}`);
    try {
      await aiContentSkipPost(scope, post._id);
      toast.success(`Day ${post.dayIndex} skipped`);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Skip failed");
    } finally {
      setBusyId(null);
    }
  };

  const unskip = async (post: AiContentPost) => {
    setBusyId(`unskip-${post._id}`);
    try {
      await aiContentUnskipPost(scope, post._id);
      toast.success(`Day ${post.dayIndex} restored — awaiting approval`);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Undo skip failed");
    } finally {
      setBusyId(null);
    }
  };

  const publishNow = async (post: AiContentPost) => {
    setBusyId(`publish-${post._id}`);
    try {
      const data = await aiContentPublishNow(scope, post._id);
      const url = data?.linkedinPostUrl;
      toast.success(
        url
          ? `Day ${post.dayIndex} published to LinkedIn`
          : `Day ${post.dayIndex} published`
      );
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Publish failed");
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const renderPostActions = (post: AiContentPost, compact = false) => (
    <div
      className={`flex shrink-0 flex-wrap gap-1.5 ${compact ? "mt-3" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      {post.status !== "published" ? (
        <>
          <Button
            variant="outline"
            size="sm"
            className={aiActionOutlineBtn}
            onClick={() => openEdit(post)}
          >
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={aiActionOutlineBtn}
            isLoading={busyId === `regen-${post._id}`}
            onClick={() =>
              setConfirm({
                kind: "regenerate",
                post,
                title: `Regenerate day ${post.dayIndex}?`,
                message:
                  "This replaces the current post text with a new AI draft for this day only (1 AI call). If the post was already approved or scheduled, it will go back to awaiting approval.",
                confirmLabel: "Regenerate",
              })
            }
          >
            Regenerate
          </Button>
        </>
      ) : null}
      {post.status === "awaiting_approval" || post.status === "draft" ? (
        <Button
          variant="primary"
          size="sm"
          className={aiActionPrimaryBtn}
          isLoading={busyId === `approve-${post._id}`}
          onClick={() =>
            setConfirm({
              kind: "approve",
              post,
              title: `Approve day ${post.dayIndex}?`,
              message:
                "This post will be scheduled for publishing at its set date and time. Nothing posts until that time.",
              confirmLabel: "Approve",
            })
          }
        >
          Approve
        </Button>
      ) : null}
      {post.status === "scheduled" || post.status === "failed" ? (
        <Button
          variant="primary"
          size="sm"
          className={aiActionPrimaryBtn}
          isLoading={busyId === `publish-${post._id}`}
          onClick={() =>
            setConfirm({
              kind: "publish_now",
              post,
              title: `Publish day ${post.dayIndex} to LinkedIn now?`,
              message:
                "This posts to your LinkedIn feed immediately (does not wait for the scheduled time). Use this to test publishing.",
              confirmLabel: "Publish now",
              danger: true,
            })
          }
        >
          Publish now
        </Button>
      ) : null}
      {post.status === "published" && post.linkedinPostUrl ? (
        <Button
          variant="outline"
          size="sm"
          className={aiActionOutlineBtn}
          onClick={() =>
            window.open(post.linkedinPostUrl || "", "_blank", "noopener,noreferrer")
          }
        >
          View on LinkedIn
        </Button>
      ) : null}
      {post.status !== "published" && post.status !== "skipped" ? (
        <Button
          variant="ghost"
          size="sm"
          className={aiActionGhostBtn}
          onClick={() =>
            setConfirm({
              kind: "skip",
              post,
              title: `Skip day ${post.dayIndex}?`,
              message:
                "This post will be removed from the schedule and will not be published. You can undo this later.",
              confirmLabel: "Skip post",
              danger: true,
            })
          }
        >
          Skip
        </Button>
      ) : null}
      {post.status === "skipped" ? (
        <Button
          variant="outline"
          size="sm"
          className={aiActionOutlineBtn}
          isLoading={busyId === `unskip-${post._id}`}
          onClick={() => void unskip(post)}
        >
          Undo skip
        </Button>
      ) : null}
    </div>
  );

  const saveEdit = async () => {
    if (!editPost) return;
    if (editText.trim().length > MAX_AI_POST_CHARS) {
      toast.error(`Max ${MAX_AI_POST_CHARS} characters`);
      return;
    }
    setBusyId(editPost._id);
    try {
      await aiContentUpdatePost(scope, {
        postId: editPost._id,
        postText: editText.trim(),
        scheduledAt: editScheduledAt
          ? new Date(editScheduledAt).toISOString()
          : undefined,
      });
      toast.success("Post updated");
      setEditPost(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await aiContentGeneratePlan(scope);
      toast.success("30-day plan ready for review");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const approveAll = async () => {
    if (!plan?._id) return;
    setBusyId("all");
    try {
      await aiContentApproveAll(scope, plan._id);
      toast.success("All posts approved and scheduled");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Approve all failed");
    } finally {
      setBusyId(null);
    }
  };

  const togglePause = async () => {
    if (!plan?._id) return;
    setBusyId("pause");
    try {
      await aiContentPausePlan(scope, plan._id);
      toast.success(
        plan.status === "paused" ? "Plan resumed" : "Plan paused — publishing stopped"
      );
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Pause failed");
    } finally {
      setBusyId(null);
    }
  };

  const runConfirmedAction = async () => {
    if (!confirm) return;
    const current = confirm;
    closeConfirm();
    if (current.kind === "approve" && current.post) {
      await approve(current.post);
      return;
    }
    if (current.kind === "skip" && current.post) {
      await skip(current.post);
      return;
    }
    if (current.kind === "regenerate" && current.post) {
      await regenerate(current.post);
      return;
    }
    if (current.kind === "publish_now" && current.post) {
      await publishNow(current.post);
      return;
    }
    if (current.kind === "approve_all") {
      await approveAll();
      return;
    }
    if (current.kind === "generate") {
      await generate();
      return;
    }
    if (current.kind === "pause" || current.kind === "resume") {
      await togglePause();
    }
  };

  if (loading) {
    return <p className="text-sm text-muted">Loading plan…</p>;
  }

  if (!plan) {
    return (
      <div className="rounded-[10px] border border-line bg-white px-6 py-12 text-center">
        <p className="text-[15px] font-semibold tracking-tight text-flow-config-text">
          No 30-day plan yet
        </p>
        <p className="mt-1 text-sm text-muted">
          Save a Content Strategy, then generate 30 posts in one AI call.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className={aiOutlineBtn}
            onClick={() => navigate("/ai-content/strategy")}
          >
            Content Strategy
          </Button>
          <Button
            variant="primary"
            size="sm"
            className={aiPrimaryBtn}
            onClick={() =>
              setConfirm({
                kind: "generate",
                title: "Generate a 30-day plan?",
                message:
                  "This creates 30 AI posts for review and uses 1 of your monthly generations. Nothing is published until you approve posts.",
                confirmLabel: "Generate plan",
              })
            }
            isLoading={generating}
            isDisabled={generations != null && generations.remaining <= 0}
          >
            Generate 30-Day Plan
          </Button>
        </div>
        {generations ? (
          <p className="mt-3 text-xs text-muted">
            Plan generations this month: {generations.used} of {generations.limit}{" "}
            used ({generations.remaining} left)
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      {attentionCount > 0 ? (
        <div className="mb-4 rounded-[8px] border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          {attentionCount} post{attentionCount === 1 ? "" : "s"} require attention
          (publish failed or blocked).
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-flow-config-text">
            30-Day Plan
          </h2>
          <p className="text-sm text-muted">
            Status: {statusLabel(plan.status)}
            {generations
              ? ` · Plan generations this month: ${generations.used} of ${generations.limit} used (${generations.remaining} left)`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-[8px] border border-line bg-white p-0.5">
            <button
              type="button"
              className={`rounded-[6px] px-3 py-1.5 text-xs font-semibold ${
                view === "list"
                  ? "bg-bg-light-blue text-brand-blue"
                  : "text-muted"
              }`}
              onClick={() => {
                setView("list");
                setStoredAiContentPlanView("list");
              }}
            >
              List
            </button>
            <button
              type="button"
              className={`rounded-[6px] px-3 py-1.5 text-xs font-semibold ${
                view === "calendar"
                  ? "bg-bg-light-blue text-brand-blue"
                  : "text-muted"
              }`}
              onClick={() => {
                setView("calendar");
                setStoredAiContentPlanView("calendar");
              }}
            >
              Calendar
            </button>
          </div>
          <Button
            variant="primary"
            size="sm"
            className={aiPrimaryBtn}
            onClick={() =>
              setConfirm({
                kind: "approve_all",
                title: "Approve all posts?",
                message:
                  "Every awaiting post in this plan will be scheduled for publishing. Skipped posts stay skipped. Nothing publishes before its scheduled time.",
                confirmLabel: "Approve all",
              })
            }
            isLoading={busyId === "all"}
          >
            Approve all
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={aiOutlineBtn}
            title={
              plan.status === "paused"
                ? "Resume automatic publishing for scheduled posts"
                : "Temporarily stop publishing. Scheduled posts will wait until you resume."
            }
            onClick={() =>
              setConfirm({
                kind: plan.status === "paused" ? "resume" : "pause",
                title:
                  plan.status === "paused" ? "Resume this plan?" : "Pause this plan?",
                message:
                  plan.status === "paused"
                    ? "Publishing will continue for approved/scheduled posts when their time arrives."
                    : "No scheduled posts will publish while the plan is paused. Your posts stay saved — resume anytime.",
                confirmLabel: plan.status === "paused" ? "Resume plan" : "Pause plan",
              })
            }
            isLoading={busyId === "pause"}
          >
            {plan.status === "paused" ? "Resume plan" : "Pause plan"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={aiOutlineBtn}
            onClick={() =>
              setConfirm({
                kind: "generate",
                title: "Generate a new 30-day plan?",
                message:
                  "This replaces your current plan with 30 new AI posts and uses 1 of your monthly generations. You will need to approve the new posts again before anything is scheduled.",
                confirmLabel: "Generate new plan",
                danger: true,
              })
            }
            isLoading={generating}
            isDisabled={generations != null && generations.remaining <= 0}
          >
            Generate new plan
          </Button>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {posts.map((post) => (
            <div
              key={post._id}
              className="rounded-[10px] border border-line bg-white p-3 text-left"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-muted">
                  Day {post.dayIndex}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass(
                    post.status
                  )}`}
                >
                  {statusLabel(post.status)}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted">
                {post.scheduledAt
                  ? new Date(post.scheduledAt).toLocaleString()
                  : "—"}
              </p>
              <p className="mt-2 line-clamp-4 text-sm text-flow-config-text">
                {post.postText}
              </p>
              {renderPostActions(post, true)}
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-line bg-white">
          <div className="divide-y divide-line">
            {posts.map((post) => (
              <div
                key={post._id}
                className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-flow-config-text">
                      Day {post.dayIndex}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClass(
                        post.status
                      )}`}
                    >
                      {statusLabel(post.status)}
                    </span>
                    <span className="text-xs text-muted">
                      {post.scheduledAt
                        ? new Date(post.scheduledAt).toLocaleString()
                        : ""}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-flow-config-text">
                    {post.postText}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {post.postText?.length || 0}/{MAX_AI_POST_CHARS}
                  </p>
                  {post.lastError &&
                  post.status !== "published" &&
                  post.status !== "skipped" ? (
                    <p className="mt-1 text-xs text-danger">{post.lastError}</p>
                  ) : null}
                </div>
                {renderPostActions(post)}
              </div>
            ))}
          </div>
        </div>
      )}

      {editPost ? (
        <div className="fixed inset-0 z-[70] flex justify-end bg-black/30">
          <div className="flex h-full w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <p className="text-sm font-semibold text-flow-config-text">
                Edit day {editPost.dayIndex}
              </p>
              <button
                type="button"
                className="text-sm text-muted hover:text-brand-blue"
                onClick={() => setEditPost(null)}
              >
                Close
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-auto p-4">
              <label className="block text-xs font-semibold text-muted">
                Scheduled date & time
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-[8px] border border-line px-3 py-2 text-sm text-flow-config-text"
                  value={editScheduledAt}
                  onChange={(e) => setEditScheduledAt(e.target.value)}
                />
                <span className="mt-1 block text-[11px] font-normal text-muted">
                  You can move this post to another day or time before it publishes.
                </span>
              </label>
              <label className="block text-xs font-semibold text-muted">
                Post text
                <textarea
                  className="mt-1 w-full rounded-[8px] border border-line px-3 py-2 text-sm text-flow-config-text"
                  rows={12}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                />
              </label>
              <p className="text-xs text-muted">
                {editText.length}/{MAX_AI_POST_CHARS}
              </p>
            </div>
            <div className="border-t border-line p-4">
              <Button
                variant="primary"
                size="sm"
                fullWidth
                className={aiPrimaryBtn}
                isLoading={busyId === editPost._id}
                onClick={() => void saveEdit()}
              >
                Save changes
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {confirm ? (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-[10px] bg-white p-6 shadow-xl">
            <p className="text-[15px] font-semibold text-flow-config-text">
              {confirm.title}
            </p>
            <p className="mt-3 text-sm text-muted">{confirm.message}</p>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className={aiOutlineBtn}
                onClick={closeConfirm}
              >
                Cancel
              </Button>
              <Button
                variant={confirm.danger ? "btn-red" : "primary"}
                size="sm"
                className={
                  confirm.danger
                    ? "h-10 px-3.5 font-semibold"
                    : aiPrimaryBtn
                }
                onClick={() => void runConfirmedAction()}
              >
                {confirm.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {generating ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40">
          <div className="rounded-[10px] bg-white px-8 py-6 text-center shadow-xl">
            <p className="text-[15px] font-semibold text-flow-config-text">
              Generating 30 posts…
            </p>
            <p className="mt-1 text-sm text-muted">
              One AI request — usually under a minute.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PlanPage;
